import {
  captureActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  type ActiveSessionAuthGenerationSnapshot,
} from './activeSessionSync';
import { hasSsoSession, type SsoContext } from './ssoSession';

type MutationGenerationStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface P1SsoLockManager {
  request<T>(
    name: string,
    options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T>;
}

export type P1SsoAuthMutationResult<T> =
  | { kind: 'completed'; value: T }
  | {
    kind: 'skipped';
    reason:
      | 'locks_unavailable'
      | 'lock_failed'
      | 'stale_intent'
      | 'storage_unavailable';
  };

const MUTATION_GENERATION_PREFIX = 'ra2n_sso_auth_mutation_generation';

/**
 * Hàng đợi thay thế khi trình duyệt không có Web Locks.
 *
 * Web Locks CHỈ tồn tại trong secure context, nên một deployment nội bộ chạy http (IP:cổng, không
 * chứng chỉ) luôn có `navigator.locks === undefined`. Khi đó runP1SsoAuthMutation trả về
 * { kind: 'skipped', reason: 'locks_unavailable' } và hậu quả không phải "kém an toàn hơn" mà là
 * TẮT HẲN: passive SSO không khởi động, đăng nhập báo "không hoàn tất an toàn".
 *
 * Lock ở đây để tuần tự hoá thao tác cookie-auth GIỮA CÁC TAB. Trình duyệt không có API đó thì
 * cũng không tab nào giành được lock để mà tranh, nên xếp hàng trong chính tab này là mức mạnh
 * nhất còn khả dụng, và vẫn giữ đúng thứ tự cho mọi thao tác cùng tên.
 */
const inTabLockQueues = new Map<string, Promise<unknown>>();

const inTabLockManager = (): P1SsoLockManager => ({
  request: <T,>(
    name: string,
    _options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T> => {
    const previous = inTabLockQueues.get(name) ?? Promise.resolve();
    const run = previous.then(
      () => callback(),
      () => callback(),
    );

    inTabLockQueues.set(
      name,
      run.then(
        () => undefined,
        () => undefined,
      ),
    );

    return run;
  },
});

const browserLockManager = (): P1SsoLockManager | undefined => {
  if (typeof navigator === 'undefined' || navigator.locks === undefined) {
    return typeof window === 'undefined' ? undefined : inTabLockManager();
  }

  return {
    request: async (name, options, callback) =>
      await navigator.locks.request(name, options, () => callback()),
  };
};

const lockName = (context: SsoContext): string =>
  `ra2n:sso-auth-mutation:${context.site}:${context.entryType}`;

const mutationGenerationKey = (context: SsoContext): string =>
  `${MUTATION_GENERATION_PREFIX}:${context.site}:${context.entryType}`;

/**
 * A terminated page can leave the durable mutation sentinel odd. Only a fresh
 * direct admin login with no local or SSO session may repair that abandoned
 * sentinel. The caller evaluates this again while holding the exact context's
 * Web Lock so a successor session can never be overwritten by the recovery.
 */
export const canRecoverAnonymousP1AdminMutation = ({
  context,
  authGeneration,
  localStorage,
  sessionStorage,
}: {
  context: SsoContext;
  authGeneration: ActiveSessionAuthGenerationSnapshot;
  localStorage: Pick<Storage, 'getItem'>;
  sessionStorage: Pick<Storage, 'getItem'>;
}): boolean =>
  context.entryType === 'admin' &&
  localStorage.getItem('site_admin_token') === null &&
  localStorage.getItem('site_admin') === null &&
  localStorage.getItem('site_admin_site') === null &&
  !hasSsoSession(context, sessionStorage) &&
  isActiveSessionAuthGenerationCurrent(authGeneration, localStorage);

/**
 * Symmetric to canRecoverAnonymousP1AdminMutation, for the clinic/unified entry types that share
 * the auth_token/auth_user/auth_clinic local session keys instead of the admin ones.
 *
 * Without this, a mutation interrupted mid-flight (tab closed, network dropped) while the
 * generation sentinel was odd permanently blocked every future clinic login from that browser —
 * AuthContext's login() never passed a `canRecoverAbandonedMutation`, so `runP1SsoAuthMutation`
 * always fell into the fail-closed default once poisoned. The admin entry already had this safety
 * valve; clinic/unified never got the symmetric fix.
 */
export const canRecoverAnonymousP1ClinicMutation = ({
  context,
  authGeneration,
  localStorage,
  sessionStorage,
}: {
  context: SsoContext;
  authGeneration: ActiveSessionAuthGenerationSnapshot;
  localStorage: Pick<Storage, 'getItem'>;
  sessionStorage: Pick<Storage, 'getItem'>;
}): boolean =>
  (context.entryType === 'clinic' || context.entryType === 'unified') &&
  localStorage.getItem('auth_token') === null &&
  localStorage.getItem('auth_user') === null &&
  localStorage.getItem('auth_clinic') === null &&
  !hasSsoSession(context, sessionStorage) &&
  isActiveSessionAuthGenerationCurrent(authGeneration, localStorage);

const readMutationGeneration = (
  context: SsoContext,
  storage: MutationGenerationStorage,
): number => {
  const stored = storage.getItem(mutationGenerationKey(context));
  if (stored === null) {
    return 0;
  }
  if (!/^\d+$/.test(stored)) {
    throw new Error('P1 SSO mutation generation storage is invalid.');
  }
  const parsed = Number(stored);
  if (
    !Number.isSafeInteger(parsed) ||
    parsed < 0 ||
    parsed > Number.MAX_SAFE_INTEGER - 2
  ) {
    throw new Error('P1 SSO mutation generation storage is invalid.');
  }
  return parsed;
};

const writeMutationGeneration = (
  context: SsoContext,
  storage: MutationGenerationStorage,
  generation: number,
): void => {
  storage.setItem(mutationGenerationKey(context), String(generation));
};

/**
 * Serializes every cookie-auth mutation for one P1 Portal entry across tabs.
 *
 * The callback boundary intentionally includes the network response, browser
 * Set-Cookie processing, local storage/state updates and generation event. A
 * queued intent is discarded after acquiring the lock when another tab has
 * already committed a newer auth generation.
 */
export const runP1SsoAuthMutation = async <T>({
  context,
  ssoEnabled,
  storage,
  operation,
  canRunAfterCommittedMutation,
  canRecoverAbandonedMutation,
  locks = browserLockManager(),
}: {
  context: SsoContext;
  ssoEnabled: boolean;
  storage: MutationGenerationStorage;
  operation: () => Promise<T>;
  /**
   * A queued exact-session operation may outlive an earlier mutation that did
   * not change the auth generation (for example an active-sync probe). The
   * caller may revalidate its exact credential snapshot under the same lock
   * and supersede only that fully committed mutation. A missing/failed check
   * keeps the default fail-closed stale-intent behavior.
   */
  canRunAfterCommittedMutation?: () => boolean;
  /**
   * Revalidate an exact anonymous context under the acquired Web Lock before
   * repairing a durable odd sentinel left by a terminated prior page.
   */
  canRecoverAbandonedMutation?: () => boolean;
  locks?: P1SsoLockManager | null;
}): Promise<P1SsoAuthMutationResult<T>> => {
  if (!ssoEnabled) {
    return { kind: 'completed', value: await operation() };
  }

  if (locks === undefined || locks === null) {
    return { kind: 'skipped', reason: 'locks_unavailable' };
  }

  let intentGeneration: ReturnType<typeof captureActiveSessionAuthGeneration>;
  let intentMutationGeneration: number;
  try {
    intentGeneration = captureActiveSessionAuthGeneration(context, storage);
    intentMutationGeneration = readMutationGeneration(context, storage);
  } catch {
    return { kind: 'skipped', reason: 'storage_unavailable' };
  }
  let callbackStarted = false;

  try {
    return await locks.request(lockName(context), { mode: 'exclusive' }, async () => {
      callbackStarted = true;
      let currentMutationGeneration: number;
      let authGenerationCurrent: boolean;
      try {
        currentMutationGeneration = readMutationGeneration(context, storage);
        authGenerationCurrent = isActiveSessionAuthGenerationCurrent(
          intentGeneration,
          storage,
        );
      } catch {
        return { kind: 'skipped', reason: 'storage_unavailable' } as const;
      }

      if (!authGenerationCurrent) {
        return { kind: 'skipped', reason: 'stale_intent' } as const;
      }

      // Odd generations are durable poison/in-progress markers. If one remains
      // after the lock becomes available, the previous mutation could not
      // commit its terminal sentinel. Recovery is safe only when the caller
      // revalidates an exact anonymous context under this same Web Lock.
      if (currentMutationGeneration % 2 !== 0) {
        let canRecover = false;
        try {
          canRecover = canRecoverAbandonedMutation?.() === true;
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        if (!canRecover) {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }

        currentMutationGeneration += 1;
        try {
          writeMutationGeneration(context, storage, currentMutationGeneration);
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        intentMutationGeneration = currentMutationGeneration;
      }

      if (currentMutationGeneration !== intentMutationGeneration) {
        let stillCurrent = false;
        try {
          stillCurrent = canRunAfterCommittedMutation?.() === true;
        } catch {
          return { kind: 'skipped', reason: 'storage_unavailable' } as const;
        }
        if (!stillCurrent) {
          return { kind: 'skipped', reason: 'stale_intent' } as const;
        }
      }

      const inProgressGeneration = currentMutationGeneration + 1;
      const committedGeneration = currentMutationGeneration + 2;
      try {
        // Claim before the request. If the terminal write fails, the odd value
        // remains and causes every later mutation to fail closed.
        writeMutationGeneration(context, storage, inProgressGeneration);
      } catch {
        return { kind: 'skipped', reason: 'storage_unavailable' } as const;
      }

      try {
        return { kind: 'completed', value: await operation() } as const;
      } finally {
        // Set-Cookie is applied before the request promise settles. Advance the
        // independent sentinel even when metadata/state/event handling throws.
        writeMutationGeneration(context, storage, committedGeneration);
      }
    });
  } catch (error) {
    if (callbackStarted) {
      throw error;
    }
    return { kind: 'skipped', reason: 'lock_failed' };
  }
};
